import {
  Button,
  Card,
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Typography,
} from "@material-tailwind/react";
import { useEffect, useMemo, useState } from "react";
import { LuPencil, LuPlusCircle } from "react-icons/lu";
import {
  LIST_HEAD_OFFICE_TABLE_HEAD,
  mailPattern,
  phoneNumber,
  strongPwd,
} from "../../variables/constants";
import DeleteIcon from "../../assets/CloseOrDelete.svg";
import { FaRegSquareCheck } from "react-icons/fa6";
import { MdOutlineMoreHoriz, MdPassword } from "react-icons/md";
import AddHeadOffice from "../../modal/AddHeadOffice";
import toast from "react-hot-toast";
import {
  deleteServices,
  getServices,
  postServices,
  putServices,
} from "../../apiServices/apiServices";
import NoDataFound from "../../components/NoDataFound";
import LoadingGif from "../../assets/loading_anim.gif";
import { FiEye, FiEyeOff, FiSearch } from "react-icons/fi";
import ChangePasswordModal from "../../modal/ChangePasswordModal";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
import { TbWorldSearch } from "react-icons/tb";
import AssignBranches from "../../modal/admin/AssignBranches";

function ManageHeadOffice() {
  const [headOfficeList, setHeadOfficeList] = useState([]);
  const [modal, setModal] = useState({
    add: false,
    update: false,
    change_password: false,
    assign_branch: false,
  });
  const [fieldData, setFieldData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [changeFieldData, setChangeFieldData] = useState({
    role: "",
    office_id: "",
    new_password: "",
    confirm_password: "",
  });
  const [isValid, setIsValid] = useState({
    email: true,
    phone: true,
    password: true,
  });
  const [showPassword, setShowPassword] = useState({});
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [loading, setLoading] = useState({ screen: false, btn: false });
  const [assignBranches, setAssignBranches] = useState([]);
  const [alreadyAssignedBranch, setAlreadyAssignedBranch] = useState([]);
  const [fieldAssignBranch, setFieldAssignBranch] = useState({
    list: [],
    headoffice_id: "",
  });
  const [selectedValues, setSelectedValues] = useState([]);
  const access_token =
    localStorage.getItem("gassol-super-admin-token") ||
    localStorage.getItem("gassol-admin-token");
  const decoded = jwtDecode(access_token);

  useEffect(() => {
    fetchHeadOfficeList(true);
  }, []);

  // Function to transform assigned branches into { value, label } format using hardware_id
  const getSelectedValues = (assignedBranches, allBranches) => {
    if (!assignedBranches || !allBranches) return [];

    return assignedBranches.map((assigned) => {
      // Extract hardware_id from assigned (string or object)
      const hardwareId =
        typeof assigned === "string" ? assigned : assigned.hardware_id;
      // Find matching branch in allBranches using hardware_id
      const branch = allBranches.find((b) => b.hardware_id === hardwareId);

      if (branch) {
        return {
          value: branch.branch,
          label: `${branch.branch_name} - ${branch.hardware_id?.slice(-4)}`,
        };
      } else {
        // Fallback: Use the assigned data as a placeholder if no match
        return {
          value: assigned?.branch,
          label: `${assigned?.branch_name} - ${hardwareId?.slice(-4)}`,
        };
      }
    });
  };

  // Update selected values when modal opens or API data changes
  useEffect(() => {
    if (modal.assign_branch) {
      if (assignBranches && alreadyAssignedBranch) {
        const values = getSelectedValues(alreadyAssignedBranch, assignBranches);
        setSelectedValues(values);
      }
    } else {
      // Reset state when modal closes
      setSelectedValues([]);
    }
  }, [modal.assign_branch, assignBranches, alreadyAssignedBranch]);

  // Memoize options to avoid recomputing unnecessarily
  const options = useMemo(
    () =>
      assignBranches?.map((branch) => ({
        value: branch.branch,
        label: `${branch.branch_name} - ${branch.hardware_id?.slice(-4)}`,
      })) || [],
    [assignBranches]
  );

  const fetchHeadOfficeList = async (load_bool) => {
    setLoading({ ...loading, screen: load_bool });
    try {
      const res = await getServices(`/api/list/my/headbranch`, access_token);
      setLoading({ ...loading, screen: false });

      if (res.responseCode === 200) {
        setHeadOfficeList(res.responseData);
      } else {
        setHeadOfficeList([]);
      }
    } catch (error) {
      setLoading({ ...loading, screen: false });
      toast.error(error);
    }
  };

  const handleopenAddModal = () => {
    setModal({ ...modal, add: true });
    setIsPasswordVisible(false);
    setFieldData({
      ...fieldData,
      name: "",
      email: "",
      phone: "",
      password: "",
    });
  };

  const handleOpenUpdateModal = (headoffice_data) => {
    setModal({ ...modal, update: true });
    setIsPasswordVisible(false);
    setFieldData({
      ...fieldData,
      name: headoffice_data.name,
      email: headoffice_data.email,
      phone: headoffice_data.phone.slice(3),
      office_id: headoffice_data._id,
    });
  };

  const handleOpenChangePasswordModal = (headoffice_data) => {
    setModal({ ...modal, change_password: true });
    setIsPasswordVisible(false);
    setChangeFieldData({
      ...changeFieldData,
      office_id: headoffice_data._id,
      role: headoffice_data.role,
      new_password: "",
      confirm_password: "",
    });
  };

  const handleChangePasswordChange = (e) => {
    if (e.target.name === "new_password") {
      if (strongPwd.test(e.target.value)) {
        setIsValid({ ...isValid, password: true });
      } else {
        setIsValid({ ...isValid, password: false });
      }
    }
    setChangeFieldData({ ...changeFieldData, [e.target.name]: e.target.value });
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    if (!isValid.password) {
      toast.error(
        "Password must have atleast 1 lowercase, number, special characters and minimum 8 characters."
      );
      setLoading({ ...loading, btn: false });
    } else if (
      changeFieldData.confirm_password !== changeFieldData.new_password
    ) {
      toast.error("New password and confirm password should match.");
    } else {
      try {
        const res = await postServices(
          `/api/change/password`,
          {
            new_pass: changeFieldData.new_password,
            cnfm_pass: changeFieldData.confirm_password,
            id: changeFieldData.office_id,
            role: changeFieldData.role,
          },
          access_token
        );
        if (res.responseCode === 200) {
          setModal({ ...modal, change_password: false });
          fetchHeadOfficeList(false);
          toast.success("Password changed.");
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        toast.error(error);
      }
    }
  };

  const handleChange = (e) => {
    if (e.target.name === "email") {
      if (mailPattern.test(e.target.value)) {
        setIsValid({ ...isValid, email: true });
      } else {
        setIsValid({ ...isValid, email: false });
      }
    } else if (e.target.name === "phone") {
      if (phoneNumber.test(e.target.value)) {
        setIsValid({ ...isValid, phone: true });
      } else {
        setIsValid({ ...isValid, phone: false });
      }
    } else if (e.target.name === "password") {
      if (strongPwd.test(e.target.value)) {
        setIsValid({ ...isValid, password: true });
      } else {
        setIsValid({ ...isValid, password: false });
      }
    }
    setFieldData({ ...fieldData, [e.target.name]: e.target.value });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    if (!isValid.phone) {
      toast.error("Please enter a valid phone number.");
      setLoading({ ...loading, btn: false });
    } else if (!isValid.email) {
      toast.error("Please enter a valid email.");
      setLoading({ ...loading, btn: false });
    } else if (!isValid.password) {
      toast.error(
        "Password must have atleast 1 lowercase, number, special characters and minimum 8 characters."
      );
      setLoading({ ...loading, btn: false });
    } else {
      try {
        const res = await postServices(
          `/api/headbranch/add`,
          {
            name: fieldData.name,
            email: fieldData.email,
            phone: "+91" + fieldData.phone,
            password: fieldData.password,
          },
          access_token
        );
        setLoading({ ...loading, btn: false });
        if (res.responseCode === 200) {
          setModal({ ...modal, add: false });
          fetchHeadOfficeList(false);
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        setLoading({ ...loading, btn: false });
        toast.error(error);
      }
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    if (!isValid.phone) {
      toast.error("Please enter a valid phone number.");
      setLoading({ ...loading, btn: false });
    } else if (!isValid.email) {
      toast.error("Please enter a valid email.");
      setLoading({ ...loading, btn: false });
    } else {
      try {
        const res = await putServices(
          `/api/headbranch/edit?headbranch_id=${fieldData.office_id}`,
          {
            name: fieldData.name,
            email: fieldData.email,
            phone: "+91" + fieldData.phone,
          },
          access_token
        );
        setLoading({ ...loading, btn: false });
        if (res.responseCode === 200) {
          setModal({ ...modal, update: false });
          fetchHeadOfficeList(false);
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        setLoading({ ...loading, btn: false });
        toast.error(error);
      }
    }
  };

  const handleDeleteHeadOffice = async (headoffice_data) => {
    if (window.confirm("Do you want to delete this company?")) {
      try {
        const res = await deleteServices(
          `/api/headbranch/delete?headbranch_id=${headoffice_data._id}`,
          access_token
        );
        if (res.responseCode === 200) {
          fetchHeadOfficeList(false);
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        toast.error(error);
      }
    }
  };

  const handleHeadOfficeSearchChange = async (e) => {
    if (e.target.value.length > 0) {
      try {
        const res = await getServices(
          `/api/list/my/headbranch?searchkey=${e.target.value}`,
          access_token
        );
        if (res.responseCode === 200) {
          setHeadOfficeList(res.responseData);
        } else {
          setHeadOfficeList([]);
        }
      } catch (error) {
        toast.error(error);
      }
    } else {
      fetchHeadOfficeList(false);
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };
  const toggleConfirmPasswordVisibility = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
  };

  const toggleAdminPasswordVisibility = (item_id) => {
    setShowPassword((prev) => ({
      ...prev,
      [item_id]: !prev[item_id],
    }));
  };

  const handleAssignBranchModal = (headoffice_data) => {
    setModal({ ...modal, assign_branch: true });
    fetchAssignBranchList(headoffice_data._id);
    fetchAlreadyAssignedBranchList(headoffice_data._id);
  };

  const fetchAssignBranchList = async (office_id) => {
    setLoading({ ...loading, btn: true });
    try {
      const res = await getServices(
        `/api/all-branch/list?headbranch_id=${office_id}`,
        access_token
      );
      setLoading({ ...loading, btn: false });

      if (res.responseCode === 200) {
        setAssignBranches(res.responseData);
        setFieldAssignBranch({
          ...fieldAssignBranch,
          headoffice_id: office_id,
        });
      } else {
        setAssignBranches([]);
        setFieldAssignBranch({
          ...fieldAssignBranch,
          headoffice_id: office_id,
        });
      }
    } catch (error) {
      setLoading({ ...loading, btn: false });
      toast.error(error);
    }
  };

  const fetchAlreadyAssignedBranchList = async (office_id) => {
    setLoading({ ...loading, btn: true });

    try {
      const res = await getServices(
        `/api/assigned-branch/list?headbranch_id=${office_id}`,
        access_token
      );
      setLoading({ ...loading, btn: false });
      if (res.responseCode === 200) {
        setAlreadyAssignedBranch(res.responseData);
      } else {
        setAlreadyAssignedBranch([]);
      }
    } catch (error) {
      setLoading({ ...loading, btn: false });
      toast.error(error);
    }
  };

  const handleAssignBatchSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    const urlencoded = new URLSearchParams();

    urlencoded.append("headbranch_id", fieldAssignBranch.headoffice_id);
    for (let i = 0; i < fieldAssignBranch.list.length; i++) {
      urlencoded.append("branch_id", fieldAssignBranch.list[i]?.value);
    }

    try {
      const res = await postServices(
        `/api/headbranch/assign`,
        urlencoded,
        access_token
      );

      setLoading({ ...loading, btn: false });
      if (res.responseCode === 200) {
        setModal({ ...modal, assign_branch: false });
        fetchHeadOfficeList(false);
      } else {
        toast.error(res.responseMessage);
      }
    } catch (error) {
      setLoading({ ...loading, btn: false });
      toast.error(error);
    }
  };

  return (
    <>
      <div className="px-6 pb-5 flex flex-col gap-6 pt-4 lg:pt-0">
        <div className="flex justify-between items-center gap-3">
          <p className="text-xl sm:text-2xl md:text-5xl font-bold">
            Head Office
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-3 relative">
          <div className="relative w-full md:w-auto">
            <input
              type="text"
              className="py-4 pl-12 pr-4 outline-none rounded-xl w-full md:min-w-[280px] xl:min-w-[350px]"
              placeholder="Search by Name"
              style={{
                boxShadow:
                  "0 4px 8px 0 rgba(0, 0, 0, 0), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
              }}
              onChange={handleHeadOfficeSearchChange}
            />
            <FiSearch className="absolute top-1/2 transform -translate-y-1/2 left-4 text-[#BDBDBD]" />
          </div>
          {decoded.role === 1 && (
            <Button
              onClick={handleopenAddModal}
              className="mt-0 font-poppins bg-[#BD3B24] font-medium text-xs sm:text-sm xl:text-base rounded-xl flex items-center gap-2 px-4 sm:px-6 ml-auto outline-none"
            >
              <LuPlusCircle />
              Add
            </Button>
          )}
        </div>

        {!loading.screen ? (
          <>
            {headOfficeList.length > 0 ? (
              <Card
                className="h-full w-full rounded-none overflow-x-scroll"
                style={{
                  boxShadow:
                    "0 4px 8px 0 rgba(0, 0, 0, 0), 0 6px 20px 0 rgba(0, 0, 0, 0.06)",
                }}
              >
                <table className="w-full min-w-max table-auto text-left">
                  <thead>
                    <tr>
                      {LIST_HEAD_OFFICE_TABLE_HEAD.map((head) => (
                        <th
                          key={head}
                          className="border-b border-blue-gray-100 bg-[#03438D] p-4"
                        >
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins text-white font-semibold uppercase"
                          >
                            {head}
                          </Typography>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {headOfficeList?.map((office, index) => {
                      return (
                        <tr key={index}>
                          <td className="p-4">
                            <Link
                              to={decoded.role === 1 ? "" : "/admin/dashboard"}
                              state={{
                                head_office: office,
                              }}
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold flex items-center gap-2 text-black text-base"
                            >
                              <FaRegSquareCheck className="text-lg text-[#BD3B24]" />
                              {office.name ?? "-"}
                            </Link>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {office.phone ?? "-"}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {office.email ?? "-"}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold text-center !block"
                            >
                              {office.subbranchCount ?? "-"}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold flex items-center gap-2"
                            >
                              {showPassword[office._id]
                                ? office.password
                                : "*".repeat(office.password.length)}
                              {!showPassword[office._id] ? (
                                <FiEyeOff
                                  onClick={() =>
                                    toggleAdminPasswordVisibility(office._id)
                                  }
                                  className="cursor-pointer"
                                />
                              ) : (
                                <FiEye
                                  onClick={() =>
                                    toggleAdminPasswordVisibility(office._id)
                                  }
                                  className="cursor-pointer"
                                />
                              )}
                            </Typography>
                          </td>
                          {decoded.role === 1 ? (
                            <td className="p-4">
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-poppins text-xl"
                              >
                                <Menu placement="left">
                                  <MenuHandler>
                                    <Button className="rounded-full p-2 bg-transparent shadow-none outline-none">
                                      <MdOutlineMoreHoriz className="text-lg text-black" />
                                    </Button>
                                  </MenuHandler>
                                  <MenuList className="p-0 w-fit min-w-[unset] border-0 shadow-none flex">
                                    <MenuItem
                                      className="w-fit font-poppins rounded-full py-2"
                                      onClick={() =>
                                        handleDeleteHeadOffice(office)
                                      }
                                    >
                                      <img src={DeleteIcon} alt="" />
                                    </MenuItem>
                                    <MenuItem
                                      title="Update Details"
                                      className="w-fit font-poppins rounded-full"
                                      onClick={() =>
                                        handleOpenUpdateModal(office)
                                      }
                                    >
                                      <LuPencil className="font-bold text-lg text-[#03438D]" />
                                    </MenuItem>
                                    <MenuItem
                                      title="Change Password"
                                      className="w-fit font-poppins rounded-full"
                                      onClick={() =>
                                        handleOpenChangePasswordModal(office)
                                      }
                                    >
                                      <MdPassword className="font-bold text-lg text-[#03438D]" />
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </Typography>
                            </td>
                          ) : (
                            <td className="p-4">
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-poppins text-xl"
                              >
                                <Menu placement="left">
                                  <MenuHandler>
                                    <Button className="rounded-full p-2 bg-transparent shadow-none outline-none">
                                      <MdOutlineMoreHoriz className="text-lg text-black" />
                                    </Button>
                                  </MenuHandler>
                                  <MenuList className="p-0 w-fit min-w-[unset] border-0 shadow-none flex">
                                    <MenuItem
                                      className="w-fit font-poppins rounded-full py-2"
                                      onClick={() =>
                                        handleAssignBranchModal(office)
                                      }
                                    >
                                      <TbWorldSearch className="text-xl" />
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </Typography>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            ) : (
              <NoDataFound />
            )}
          </>
        ) : (
          <div className="w-full flex justify-center items-center h-[60vh]">
            <img src={LoadingGif} alt="" />
          </div>
        )}
      </div>

      <AddHeadOffice
        open={modal.add}
        onClose={() => setModal({ ...modal, add: false })}
        modal={modal}
        togglePasswordVisibility={togglePasswordVisibility}
        isPasswordVisible={isPasswordVisible}
        fieldData={fieldData}
        handleChange={handleChange}
        handleAddSubmit={handleAddSubmit}
        loading={loading}
      />

      <AddHeadOffice
        open={modal.update}
        onClose={() => setModal({ ...modal, update: false })}
        modal={modal}
        fieldData={fieldData}
        handleChange={handleChange}
        handleUpdateSubmit={handleUpdateSubmit}
        loading={loading}
      />

      <ChangePasswordModal
        open={modal.change_password}
        onClose={() => setModal({ ...modal, change_password: false })}
        isPasswordVisible={isPasswordVisible}
        isConfirmPasswordVisible={isConfirmPasswordVisible}
        togglePasswordVisibility={togglePasswordVisibility}
        toggleConfirmPasswordVisibility={toggleConfirmPasswordVisibility}
        handleChangePasswordChange={handleChangePasswordChange}
        handleChangePasswordSubmit={handleChangePasswordSubmit}
      />

      <AssignBranches
        open={modal.assign_branch}
        onClose={() => setModal({ ...modal, assign_branch: false })}
        assignBranches={assignBranches}
        handleAssignBatchSubmit={handleAssignBatchSubmit}
        loading={loading}
        setFieldAssignBranch={setFieldAssignBranch}
        fieldAssignBranch={fieldAssignBranch}
        alreadyAssignedBranch={alreadyAssignedBranch}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        options={options}
      />
    </>
  );
}

export default ManageHeadOffice;
